import React, { useState, useEffect } from 'react';
import { timeEntriesAPI, casesAPI, employeesAPI, tasksAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import PageHeader from '../components/PageHeader';
import Modal from '../components/Modal';
import StatusBadge from '../components/StatusBadge';
import { Plus, Search, Edit2, Trash2, Clock, DollarSign } from 'lucide-react';
import { toast } from 'react-toastify';

const EMPTY = {
  case: '', task: '', employee: '', date: new Date().toISOString().slice(0, 10),
  start_time: '', end_time: '', duration_hours: 0, description: '',
  billing_status: 'Unbilled', billable: true, hourly_rate: 0,
};

export default function TimeEntries() {
  const [entries, setEntries] = useState([]);
  const [cases, setCases] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [billingFilter, setBillingFilter] = useState('');
  const { user } = useAuth();

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (billingFilter) params.billing_status = billingFilter;
      const { data } = await timeEntriesAPI.list(params);
      setEntries(data.results || data);
    } finally { setLoading(false); }
  };

  useEffect(() => {
    load();
    casesAPI.list().then(r => setCases(r.data.results || r.data));
    employeesAPI.list().then(r => setEmployees(r.data.results || r.data));
  }, []);
  useEffect(() => { load(); }, [billingFilter]);

  const calcDuration = (start, end) => {
    if (!start || !end) return 0;
    const [sh, sm] = start.split(':').map(Number);
    const [eh, em] = end.split(':').map(Number);
    return Math.max(0, ((eh * 60 + em) - (sh * 60 + sm)) / 60);
  };

  const handleTimeChange = (field, val) => {
    const updated = { ...form, [field]: val };
    if (updated.start_time && updated.end_time) {
      updated.duration_hours = calcDuration(updated.start_time, updated.end_time).toFixed(2);
    }
    setForm(updated);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form };
      if (!payload.employee) payload.employee = user.id;
      if (!payload.hourly_rate) {
        const emp = employees.find(e => e.id == payload.employee);
        payload.hourly_rate = emp?.hourly_rate || 0;
      }
      if (editing) { await timeEntriesAPI.update(editing.id, payload); toast.success('Updated'); }
      else { await timeEntriesAPI.create(payload); toast.success('Time entry added'); }
      setModalOpen(false);
      load();
    } catch { toast.error('Failed to save'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this time entry?')) return;
    await timeEntriesAPI.delete(id);
    toast.success('Deleted');
    load();
  };

  const totalHours = entries.reduce((s, e) => s + parseFloat(e.duration_hours || 0), 0);
  const totalAmount = entries.reduce((s, e) => s + parseFloat(e.amount || 0), 0);
  const unbilledHours = entries.filter(e => e.billing_status === 'Unbilled').reduce((s, e) => s + parseFloat(e.duration_hours || 0), 0);

  const f = (name) => ({ value: form[name] ?? '', onChange: e => setForm({ ...form, [name]: e.target.value }) });

  return (
    <div className="p-6">
      <PageHeader
        title="Time Tracking"
        subtitle="Track billable and non-billable time"
        actions={<button className="btn-primary" onClick={() => { setEditing(null); setForm({ ...EMPTY, employee: user.id }); setModalOpen(true); }}><Plus size={16} />Log Time</button>}
      />

      {/* Summary Cards */}
      <div className="grid grid-cols-3 gap-4 mb-5">
        <div className="card text-center">
          <div className="text-2xl font-bold text-blue-700">{totalHours.toFixed(1)}h</div>
          <div className="text-xs text-gray-500 mt-1">Total Hours</div>
        </div>
        <div className="card text-center">
          <div className="text-2xl font-bold text-amber-600">{unbilledHours.toFixed(1)}h</div>
          <div className="text-xs text-gray-500 mt-1">Unbilled Hours</div>
        </div>
        <div className="card text-center">
          <div className="text-2xl font-bold text-green-700">${totalAmount.toFixed(2)}</div>
          <div className="text-xs text-gray-500 mt-1">Total Value</div>
        </div>
      </div>

      <div className="flex gap-3 mb-5">
        <select className="input w-48" value={billingFilter} onChange={e => setBillingFilter(e.target.value)}>
          <option value="">All Status</option>
          {['Unbilled', 'Billed', 'Written Off'].map(s => <option key={s}>{s}</option>)}
        </select>
      </div>

      <div className="card p-0 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b">
              <tr>{['Date', 'Case', 'Staff', 'Description', 'Hours', 'Rate', 'Amount', 'Status', ''].map(h => (
                <th key={h} className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">{h}</th>
              ))}</tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading ? <tr><td colSpan={9} className="text-center py-12 text-gray-400">Loading...</td></tr>
              : entries.length === 0 ? <tr><td colSpan={9} className="text-center py-12 text-gray-400">No time entries yet.</td></tr>
              : entries.map(e => (
                <tr key={e.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-gray-600">{e.date}</td>
                  <td className="px-4 py-3 text-gray-700 max-w-xs truncate">{e.case_title || '—'}</td>
                  <td className="px-4 py-3 text-gray-600">{e.employee_name}</td>
                  <td className="px-4 py-3 text-gray-600 max-w-xs truncate">{e.description}</td>
                  <td className="px-4 py-3 font-semibold">{e.duration_hours}h</td>
                  <td className="px-4 py-3 text-gray-500">${e.hourly_rate}/h</td>
                  <td className="px-4 py-3 font-semibold text-green-700">${parseFloat(e.amount || 0).toFixed(2)}</td>
                  <td className="px-4 py-3"><StatusBadge status={e.billing_status} /></td>
                  <td className="px-4 py-3">
                    <div className="flex gap-1">
                      <button className="p-1.5 text-gray-400 hover:text-amber-600" onClick={() => { setEditing(e); setForm({ ...e }); setModalOpen(true); }}><Edit2 size={14} /></button>
                      <button className="p-1.5 text-gray-400 hover:text-red-600" onClick={() => handleDelete(e.id)}><Trash2 size={14} /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <Modal isOpen={modalOpen} onClose={() => setModalOpen(false)} title={editing ? 'Edit Time Entry' : 'Log Time'}>
        <form onSubmit={handleSubmit} className="grid grid-cols-2 gap-4">
          <div>
            <label className="label">Case *</label>
            <select className="input" required {...f('case')}>
              <option value="">Select case</option>
              {cases.map(c => <option key={c.id} value={c.id}>{c.case_title}</option>)}
            </select>
          </div>
          <div>
            <label className="label">Staff</label>
            <select className="input" {...f('employee')}>
              <option value="">Select staff</option>
              {employees.map(e => <option key={e.id} value={e.id}>{e.full_name}</option>)}
            </select>
          </div>
          <div>
            <label className="label">Date *</label>
            <input type="date" className="input" required {...f('date')} />
          </div>
          <div>
            <label className="label">Duration (hours) *</label>
            <input type="number" step="0.25" min="0" className="input" required {...f('duration_hours')} />
          </div>
          <div>
            <label className="label">Start Time</label>
            <input type="time" className="input" value={form.start_time || ''} onChange={e => handleTimeChange('start_time', e.target.value)} />
          </div>
          <div>
            <label className="label">End Time</label>
            <input type="time" className="input" value={form.end_time || ''} onChange={e => handleTimeChange('end_time', e.target.value)} />
          </div>
          <div>
            <label className="label">Hourly Rate (R)</label>
            <input type="number" step="0.01" min="0" className="input" {...f('hourly_rate')} />
          </div>
          <div>
            <label className="label">Billing Status</label>
            <select className="input" {...f('billing_status')}>
              {['Unbilled', 'Billed', 'Written Off'].map(s => <option key={s}>{s}</option>)}
            </select>
          </div>
          <div className="col-span-2">
            <label className="label">Description *</label>
            <textarea className="input" rows={3} required value={form.description || ''} onChange={e => setForm({ ...form, description: e.target.value })} />
          </div>
          <div className="col-span-2 flex items-center gap-2">
            <input type="checkbox" id="billable_te" checked={form.billable === true || form.billable === 'true'} onChange={e => setForm({ ...form, billable: e.target.checked })} className="rounded" />
            <label htmlFor="billable_te" className="text-sm text-gray-700">Billable</label>
          </div>
          <div className="col-span-2 flex justify-end gap-3 pt-2">
            <button type="button" className="btn-secondary" onClick={() => setModalOpen(false)}>Cancel</button>
            <button type="submit" className="btn-primary" disabled={saving}>{saving ? 'Saving...' : 'Save Entry'}</button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
